import type {
  Employee,
  Recognition,
  BadgeItem,
  LeaderboardEntry,
  Reward,
  CompanyValue,
  RecognitionTypeConfig,
  RecognitionStats,
} from './types';

export const teams = ['Platform', 'Core', 'Growth', 'Frontend', 'Infrastructure', 'People Ops', 'Revenue']
export const departments = ['Engineering', 'Product', 'Design', 'Marketing', 'Sales', 'HR', 'Finance']

export const employees: Employee[] = [
  { id: '1', name: 'Priya Sharma', dept: 'Engineering', team: 'Platform', title: 'Sr. Software Engineer', initials: 'PS' },
  { id: '2', name: 'Arjun Mehta', dept: 'Product', team: 'Growth', title: 'Product Manager', initials: 'AM' },
  { id: '3', name: 'Kavya Reddy', dept: 'Design', team: 'Growth', title: 'UX Lead', initials: 'KR' },
  { id: '4', name: 'Vikram Patel', dept: 'Engineering', team: 'Core', title: 'Tech Lead', initials: 'VP' },
  { id: '5', name: 'Ananya Iyer', dept: 'Marketing', team: 'Revenue', title: 'Marketing Director', initials: 'AI' },
  { id: '6', name: 'Rohan Das', dept: 'Sales', team: 'Revenue', title: 'Sales Manager', initials: 'RD' },
  { id: '7', name: 'Meera Joshi', dept: 'HR', team: 'People Ops', title: 'HR Business Partner', initials: 'MJ' },
  { id: '8', name: 'Siddharth Nair', dept: 'Engineering', team: 'Platform', title: 'Backend Engineer', initials: 'SN' },
  { id: '9', name: 'Divya Krishnan', dept: 'Finance', team: 'People Ops', title: 'Finance Analyst', initials: 'DK' },
  { id: '10', name: 'Aditya Rao', dept: 'Engineering', team: 'Infrastructure', title: 'DevOps Lead', initials: 'AR' },
];

export const companyValues: CompanyValue[] = [
  'Innovation',
  'Teamwork',
  'Customer First',
  'Integrity',
  'Excellence',
  'Growth Mindset',
];

export const VALUE_COLORS: Record<CompanyValue, string> = {
  Innovation: '#6C5CE7',
  Teamwork: '#4FC3F7',
  'Customer First': '#66BB6A',
  Integrity: '#F5A623',
  Excellence: '#FF6B6B',
  'Growth Mindset': '#AB47BC',
};

export const recognitionTypes: RecognitionTypeConfig[] = [
  { id: 'kudos', label: 'Kudos', icon: '👏', desc: 'Peer-to-peer appreciation' },
  { id: 'spot_award', label: 'Spot Award', icon: '🏅', desc: 'Manager award with points' },
  { id: 'shoutout', label: 'Shout-out', icon: '📣', desc: 'Public appreciation' },
  { id: 'thankyou', label: 'Thank You', icon: '💜', desc: 'Private gratitude' },
];

export const recognitions: Recognition[] = [
  {
    id: '1',
    sender: employees[0],
    recipients: [employees[1]],
    type: 'kudos',
    message:
      'Incredible job leading the sprint planning session! Your clarity on priorities helped the entire team align and deliver on time. The way you handled conflicting requirements was truly impressive.',
    value: 'Teamwork',
    badge: 'Team Player',
    reactions: { clap: 12, heart: 8, celebrate: 5, fire: 3 },
    comments: [
      { id: 'c1', user: employees[3], text: 'Well deserved! The sprint went so smoothly thanks to Arjun.', time: '1 hour ago' },
      { id: 'c2', user: employees[4], text: 'Couldn\'t agree more, great leadership!', time: '1 hour ago' },
      { id: 'c3', user: employees[7], text: 'Arjun always brings clarity to complex situations.', time: '45 min ago' },
      { id: 'c4', user: employees[1], text: 'Thank you Priya! Means a lot coming from you.', time: '30 min ago' },
    ],
    time: '2 hours ago',
  },
  {
    id: '2',
    sender: employees[2],
    recipients: [employees[3]],
    type: 'spot_award',
    message:
      'For the outstanding architecture redesign that reduced API latency by 60%. This directly impacted customer satisfaction scores!',
    value: 'Innovation',
    badge: 'Innovator',
    reactions: { clap: 24, heart: 15, celebrate: 18, fire: 9 },
    comments: [
      { id: 'c5', user: employees[0], text: 'That latency improvement was incredible!', time: '4 hours ago' },
      { id: 'c6', user: employees[9], text: 'The infra team noticed the difference immediately.', time: '3 hours ago' },
      { id: 'c7', user: employees[1], text: 'This is what innovation looks like.', time: '2 hours ago' },
    ],
    time: '5 hours ago',
    points: 500,
  },
  {
    id: '3',
    sender: employees[4],
    recipients: [employees[5]],
    type: 'kudos',
    message:
      'Thank you for going above and beyond with the enterprise demo yesterday. The client was blown away by your product knowledge and professionalism!',
    value: 'Customer First',
    badge: null,
    reactions: { clap: 8, heart: 11, celebrate: 3, fire: 2 },
    comments: [
      { id: 'c8', user: employees[6], text: 'Rohan always nails the demos!', time: 'Yesterday' },
      { id: 'c9', user: employees[2], text: 'The client sent a thank-you email too!', time: 'Yesterday' },
    ],
    time: 'Yesterday',
  },
  {
    id: '4',
    sender: employees[6],
    recipients: [employees[0]],
    type: 'milestone',
    message:
      'Celebrating 5 years of incredible contributions at the company! Your journey from junior engineer to senior has been nothing short of inspiring.',
    value: 'Excellence',
    badge: '5 Year Veteran',
    reactions: { clap: 45, heart: 32, celebrate: 28, fire: 15 },
    comments: [
      { id: 'c10', user: employees[1], text: 'Happy 5 years Priya! Here\'s to many more!', time: '2 days ago' },
      { id: 'c11', user: employees[3], text: 'You\'ve been such an inspiration to the whole team.', time: '2 days ago' },
      { id: 'c12', user: employees[7], text: 'Congratulations! Well earned.', time: '2 days ago' },
    ],
    time: '2 days ago',
  },
  {
    id: '5',
    sender: employees[7],
    recipients: [employees[8]],
    type: 'shoutout',
    message:
      'Huge shoutout for catching that critical financial discrepancy before month-end close. Your attention to detail saved us from a major headache!',
    value: 'Integrity',
    badge: null,
    reactions: { clap: 6, heart: 9, celebrate: 2, fire: 1 },
    comments: [
      { id: 'c13', user: employees[6], text: 'Great catch Divya!', time: '3 days ago' },
    ],
    time: '3 days ago',
  },
];

export const badges: BadgeItem[] = [
  { id: '1', name: 'Innovator', icon: '💡', category: 'Values', rarity: 'Rare', holders: 23, desc: 'Recognized for driving innovation', earned: true },
  { id: '2', name: 'Team Player', icon: '🤝', category: 'Values', rarity: 'Common', holders: 89, desc: 'Celebrated for exceptional teamwork', earned: true },
  { id: '3', name: '5 Year Veteran', icon: '🏆', category: 'Milestones', rarity: 'Epic', holders: 12, desc: 'Five years of dedicated service', earned: true },
  { id: '4', name: 'Customer Champion', icon: '⭐', category: 'Values', rarity: 'Rare', holders: 31, desc: 'Going above and beyond for customers', earned: false },
  { id: '5', name: 'Mentor', icon: '🎓', category: 'Skills', rarity: 'Epic', holders: 8, desc: 'Guiding and developing others', earned: false },
  { id: '6', name: 'Fire Starter', icon: '🔥', category: 'Special', rarity: 'Legendary', holders: 3, desc: 'Igniting change across the org', earned: false },
  { id: '7', name: 'First Kudos', icon: '🌟', category: 'Milestones', rarity: 'Common', holders: 245, desc: 'Sent your very first recognition', earned: true },
  { id: '8', name: 'Streak Master', icon: '⚡', category: 'Special', rarity: 'Epic', holders: 15, desc: '10-week recognition streak', earned: false },
];

export const leaderboardData: LeaderboardEntry[] = [
  { rank: 1, employee: employees[0], count: 47, points: 2350, xp: 4700, topValue: 'Innovation', trend: 'up' },
  { rank: 2, employee: employees[3], count: 42, points: 2100, xp: 4200, topValue: 'Teamwork', trend: 'up' },
  { rank: 3, employee: employees[1], count: 38, points: 1900, xp: 3800, topValue: 'Customer First', trend: 'same' },
  { rank: 4, employee: employees[4], count: 35, points: 1750, xp: 3500, topValue: 'Excellence', trend: 'down' },
  { rank: 5, employee: employees[2], count: 31, points: 1550, xp: 3100, topValue: 'Innovation', trend: 'up' },
  { rank: 6, employee: employees[7], count: 28, points: 1400, xp: 2800, topValue: 'Integrity', trend: 'up' },
  { rank: 7, employee: employees[5], count: 24, points: 1200, xp: 2400, topValue: 'Teamwork', trend: 'down' },
  { rank: 8, employee: employees[8], count: 21, points: 1050, xp: 2100, topValue: 'Excellence', trend: 'same' },
];

export const rewards: Reward[] = [
  { id: '1', name: 'Extra PTO Day', points: 500, category: 'Internal', icon: '🏖️', desc: 'One additional paid time off day', available: true },
  { id: '2', name: 'Amazon Gift Card', points: 1000, category: 'Gift Cards', icon: '🎁', desc: '$50 Amazon gift card', available: true },
  { id: '3', name: 'Lunch with CEO', points: 2000, category: 'Experiences', icon: '🍽️', desc: 'Exclusive lunch with leadership', available: true },
  { id: '4', name: 'Learning Course', points: 300, category: 'Internal', icon: '📚', desc: 'Enroll in any premium course', available: true },
  { id: '5', name: 'WFH Friday', points: 200, category: 'Internal', icon: '🏠', desc: 'Work from home on any Friday', available: true },
  { id: '6', name: 'Parking Upgrade', points: 400, category: 'Internal', icon: '🅿️', desc: 'Premium parking spot for a month', available: true },
  { id: '7', name: 'Charity Donation', points: 250, category: 'Charity', icon: '❤️', desc: 'Donate to a charity of choice', available: true },
  { id: '8', name: 'Payroll Bonus', points: 5000, category: 'Internal', icon: '💰', desc: '₹10,000 added to next payroll', available: true },
];

export const currentUserStats: RecognitionStats = {
  received: 24,
  given: 18,
  points: 1250,
  xp: 3420,
};

export const analyticsChartData = [12, 18, 15, 22, 28, 25, 34, 31, 38, 42, 39, 45];
export const analyticsMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const deptAnalytics = [
  { dept: 'Engineering', given: 145, received: 168 },
  { dept: 'Product', given: 98, received: 112 },
  { dept: 'Marketing', given: 76, received: 84 },
  { dept: 'Sales', given: 67, received: 59 },
  { dept: 'HR', given: 54, received: 48 },
  { dept: 'Finance', given: 32, received: 38 },
];

export const valueDistribution: { value: CompanyValue; pct: number }[] = [
  { value: 'Innovation', pct: 28 },
  { value: 'Teamwork', pct: 22 },
  { value: 'Customer First', pct: 18 },
  { value: 'Integrity', pct: 14 },
  { value: 'Excellence', pct: 10 },
  { value: 'Growth Mindset', pct: 8 },
];
